import {computed, ref, watch} from "vue"
import {defineStore} from "pinia"
import {useFetch, type ExtendedFetchOptions} from "../fetch"
import type {GuestRestaurantInfo} from "../guests/types"
import {useErrorStore} from "../error"
import {Restaurant} from "./models/restaurant.model"
import type {CrmRestaurantMainInfo, RestaurantShortInfo} from "./types"
import {useLocalStorage} from "@vueuse/core"

interface RestaurantPayload {
  restaurants_id: string
  restaurants_title: string
  restaurants_logo_url: string
  info: CrmRestaurantMainInfo
}

export const useRestaurantStore = defineStore("restaurant", () => {
  const errorStore = useErrorStore()
  // state
  const restaurant = ref({} as GuestRestaurantInfo)
  const currentRestaurantId = ref(useLocalStorage("currentRestaurantId", ""))
  const restaurantSideBarInfo = ref({
    id: "",
    logoURL: "",
    title: "",
    expirenzaUuid: "",
  } as GuestRestaurantInfo)
  const restaurantsList = ref([] as Restaurant[])

  const loading = ref(false)

  // getters
  const getRestaurantList = computed(() => {
    return restaurantsList.value?.map(
      (r) =>
        ({
          id: r.restaurantId,
          title: r.restaurantTitle,
          iconUrl: r.restaurantLogoUrl,
          address: r.info?.location?.formattedAddress || "",
        }) as RestaurantShortInfo
    )
  })

  // actions
  async function fetchRestaurantById(restaurantId: string) {
    if (!restaurantId) return

    loading.value = true

    const {error, data} = await useFetch(`/api/clients/menu/restaurant/${restaurantId}`, {
      method: "GET",
      $skipEncryption: false,
      $useToken: true,
    } as ExtendedFetchOptions).get()

    if (data.value) {
      setRestaurantInfo(data.value.result)
      restaurantSideBarInfo.value = {
        id: restaurantId,
        logoURL: data.value.result.iconUrl,
        title: data.value.result.title,
        expirenzaUuid: restaurantId, // ?
      }
    }

    loading.value = false

    if (error.value) {
      setRestaurantInfo({} as GuestRestaurantInfo)
      errorStore.setError({
        error: error.value,
        statusCode: error.value.statusCode,
        errTitle: error.value.errTitle || error.value.errCode,
        errText: error.value.errText,
      })
    }

    return {
      data,
      error,
      loading,
    }
  }

  async function fetchRestaurantsList() {
    loading.value = true

    const {error, data} = await useFetch(`/api/crm-analytics/auth-api/restaurants`, {
      method: "GET",
      $skipEncryption: false,
      $useToken: true,
    } as ExtendedFetchOptions).get()

    if (data.value) {
      restaurantsList.value = []
      data.value.result?.forEach((r: RestaurantPayload) =>
        restaurantsList.value.push(new Restaurant(r))
      )
    }

    loading.value = false

    if (error.value) {
      restaurantsList.value = []
      errorStore.setError({
        error: error.value,
        statusCode: error.value?.statusCode,
        errTitle: error.value?.errTitle || error.value?.errCode,
        errText: error.value?.errText,
      })
    }

    return {
      data,
      error,
      loading,
    }
  }

  function setRestaurantInfo(info: GuestRestaurantInfo) {
    restaurant.value = {...info}
  }

  watch(
    currentRestaurantId,
    (newV) => {
      if (newV) {
        fetchRestaurantById(newV)
      }
    },
    {immediate: true}
  )

  return {
    restaurant,
    restaurantsList,
    getRestaurantList,
    setRestaurantInfo,
    restaurantSideBarInfo,
    currentRestaurantId,
    fetchRestaurantById,
    fetchRestaurantsList,
  }
})
